import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { 
  Key, 
  Rocket, 
  Brain, 
  Shield, 
  ChevronRight, 
  ArrowLeft,
  BookOpen,
  Loader2
} from 'lucide-react';
import { loadAllProductArticles, loadProductArticleById, ProductArticle } from '../product-docs';
import Navbar from '../components/Navbar';

// 图标映射
const iconMap: Record<string, React.ReactNode> = {
  'Rocket': <Rocket className="w-5 h-5" />,
  'Key': <Key className="w-5 h-5" />,
  'Brain': <Brain className="w-5 h-5" />,
  'Shield': <Shield className="w-5 h-5" />,
};

/**
 * SEO 组件 - 动态设置页面标题和 meta 标签
 */
const ProductSEO: React.FC<{ article: ProductArticle | null }> = ({ article }) => {
  useEffect(() => {
    if (article) {
      document.title = `${article.title} | 产品 | Teegal`;
      
      const description = article.description || article.content.slice(0, 200).replace(/<[^>]*>/g, '').replace(/[#*_`]/g, '').trim();
      updateMetaTag('description', description);
      updateMetaTag('og:title', article.title);
      updateMetaTag('og:description', description);
      updateMetaTag('og:type', 'article');
      updateMetaTag('og:url', `https://www.workbees.space/#/product?id=${article.id}`);
    } else {
      document.title = '产品 | Teegal';
      updateMetaTag('description', '了解 Teegal 的产品功能、使用指南和最佳实践');
      updateMetaTag('og:title', '产品 | Teegal');
      updateMetaTag('og:description', '了解 Teegal 的产品功能、使用指南和最佳实践');
    }
  }, [article]);

  return null;
};

function updateMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    if (name.startsWith('og:')) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    document.head.appendChild(meta);
  }
  meta.content = content;
}

const ProductIntro: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<ProductArticle[]>([]);
  const [currentArticle, setCurrentArticle] = useState<ProductArticle | null>(null);
  const [loading, setLoading] = useState(true);

  // 从 URL 查询参数读取文章 ID
  useEffect(() => {
    const articleId = searchParams.get('id');
    if (articleId && articles.length > 0) {
      loadProductArticleById(articleId).then(article => {
        if (article) {
          setCurrentArticle(article);
        }
      });
    }
  }, [searchParams, articles]);

  // 加载所有文章
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const loadedArticles = await loadAllProductArticles();
      setArticles(loadedArticles);
      
      // 如果没有指定文章 ID，默认显示第一篇
      if (!searchParams.get('id') && loadedArticles.length > 0) {
        setCurrentArticle(loadedArticles[0]);
      }
      
      setLoading(false);
    };
    init();
  }, []);

  const handleArticleClick = (article: ProductArticle) => {
    setCurrentArticle(article);
    setSearchParams({ id: article.id });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 自定义 Markdown 组件渲染
  const markdownComponents = {
    h1: ({ children }: { children?: React.ReactNode }) => (
      <h1 className="text-3xl font-bold mt-8 mb-6 text-slate-800">{children}</h1>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <h2 className="text-2xl font-bold mt-8 mb-4 text-slate-800 flex items-center gap-2">
        <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
        {children}
      </h2>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-800">{children}</h3>
    ),
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="mb-4 text-slate-600 leading-relaxed">{children}</p>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul className="list-disc list-inside my-4 space-y-2 text-slate-700">{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol className="list-decimal list-inside my-4 space-y-2 text-slate-700">{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <li className="text-slate-600">{children}</li>
    ),
    code: ({ inline, children, ...props }: { inline?: boolean; children?: React.ReactNode }) => {
      if (inline) {
        return (
          <code className="px-1.5 py-0.5 mx-0.5 rounded bg-slate-100 text-indigo-600 font-mono text-sm border border-indigo-100" {...props}>
            {children}
          </code>
        );
      }
      return (
        <pre className="block px-4 py-3 my-4 rounded-lg bg-slate-900 text-slate-100 font-mono text-sm overflow-auto border border-slate-700 max-h-60">
          <code {...props}>{children}</code>
        </pre>
      );
    },
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote className="border-l-4 border-indigo-300 bg-indigo-50 pl-4 pr-4 py-3 my-6 italic text-slate-600 rounded-r">
        {children}
      </blockquote>
    ),
    table: ({ children }: { children?: React.ReactNode }) => (
      <table className="w-full my-6 border-collapse border border-slate-200 rounded-lg overflow-hidden">
        {children}
      </table>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <th className="border-b-2 border-slate-300 px-4 py-3 text-left font-semibold text-slate-800 bg-slate-50">
        {children}
      </th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <td className="border-b border-slate-200 px-4 py-3 text-slate-600">
        {children}
      </td>
    ),
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <a 
        href={href} 
        className="text-indigo-600 underline underline-offset-4 hover:text-indigo-700 transition-colors"
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    ),
    hr: () => <hr className="my-8 border-t-2 border-slate-200" />,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          加载中...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-14">
      {/* SEO 组件 */}
      <ProductSEO article={currentArticle} />
      
      {/* Navbar */}
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Article Navigation */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden sticky top-20">
              <div className="p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <h2 className="font-semibold flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  产品文档
                </h2>
                <p className="text-sm text-indigo-100 mt-1">
                  了解如何使用 Teegal
                </p>
              </div>
              
              <nav className="p-2">
                {articles.map((article) => (
                  <button
                    key={article.id}
                    onClick={() => handleArticleClick(article)}
                    className={`w-full flex items-start gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                      currentArticle?.id === article.id
                        ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className={`mt-0.5 ${currentArticle?.id === article.id ? 'text-indigo-600' : 'text-slate-400'}`}>
                      {article.icon && iconMap[article.icon] || <BookOpen className="w-5 h-5" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{article.title}</div>
                      <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">
                        {article.description}
                      </div>
                    </div>
                    {currentArticle?.id === article.id && (
                      <ChevronRight className="w-4 h-4 flex-shrink-0 mt-1" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

          </aside>

          {/* Right Content Area */}
          <div className="flex-1">
            {currentArticle ? (
              <article className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
                {/* Article Header */}
                <header className="mb-8 pb-6 border-b border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                      {currentArticle.icon && iconMap[currentArticle.icon] || <BookOpen className="w-6 h-6" />}
                    </div>
                    <div>
                      <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">
                        {currentArticle.title}
                      </h1>
                    </div>
                  </div>
                  <p className="text-slate-500 text-lg">
                    {currentArticle.description}
                  </p>
                </header>

                {/* Article Body */}
                <div className="prose prose-slate max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={markdownComponents}
                  >
                    {currentArticle.content}
                  </ReactMarkdown>
                </div>

                {/* Article Navigation */}
                <footer className="mt-12 pt-8 border-t border-slate-200">
                  <div className="flex flex-col sm:flex-row gap-4 justify-between">
                    {(() => {
                      const currentIndex = articles.findIndex(a => a.id === currentArticle.id);
                      const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
                      const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;
                      
                      return (
                        <>
                          {prevArticle ? (
                            <button
                              onClick={() => handleArticleClick(prevArticle)}
                              className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 transition-colors"
                            >
                              <ArrowLeft className="w-4 h-4" />
                              <span className="text-left">
                                <div className="text-xs text-slate-400">上一篇</div>
                                <div className="font-medium">{prevArticle.title}</div>
                              </span>
                            </button>
                          ) : (
                            <div />
                          )}
                          
                          {nextArticle && (
                            <button
                              onClick={() => handleArticleClick(nextArticle)}
                              className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 transition-colors sm:text-right sm:flex-row-reverse"
                            >
                              <ChevronRight className="w-4 h-4" />
                              <span>
                                <div className="text-xs text-slate-400">下一篇</div>
                                <div className="font-medium">{nextArticle.title}</div>
                              </span>
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </footer>
              </article>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-700 mb-2">
                  暂无文档
                </h2>
                <p className="text-slate-500">
                  产品文档正在准备中，敬请期待
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProductIntro;
